import axios from 'axios';
import { getCurrentUser } from './authService';
import config from '../config/config';

const API_URL = config.api.baseUrl + config.api.dataEndpoint;
const SOCIAL_API_URL = config.api.baseUrl + config.api.socialEndpoint;
const AI_FEATURE_QUOTA_CACHE_TTL_MS = 5000;
const aiFeatureQuotaRequestCache = new Map();
const aiFeatureQuotaResponseCache = new Map();

const authHeader = () => {
  const user = getCurrentUser();
  if (user && user.token) {
    return { 'x-auth-token': user.token };
  } else {
    return {};
  }
};

export const updateUserLocation = (location) => {
  return axios.post(`${API_URL}/profile/update-location`, { location }, { headers: authHeader() });
};

export const updateUserTimezone = (timezone) => {
  return axios.post(`${API_URL}/profile/timezone`, { timezone }, { headers: authHeader() });
};

export const submitSupportRequest = (formData) => {
  const user = getCurrentUser();
  const headers = {
    'x-auth-token': user?.token,
    'Content-Type': 'multipart/form-data',
  };
  return axios.post(`${API_URL}/support/contact`, formData, { headers });
};

export const verifyQuestWithAudio = (questId, audioFile) => {
  const formData = new FormData();
  formData.append('questId', questId);
  formData.append('audio', audioFile);

  const user = getCurrentUser();
  const headers = {
    'x-auth-token': user.token,
    'Content-Type': 'multipart/form-data',
  };

  return axios.post(`${API_URL}/quests/verify-audio`, formData, { headers });
};

// --- LEADERBOARD ---
export const getLeaderboard = (filter) => {
  return axios.get(`${SOCIAL_API_URL}/leaderboard?filter=${filter}`, { headers: authHeader() });
};

export const getMyDuelState = () => {
  return axios.get(`${SOCIAL_API_URL}/duels/me`, { headers: authHeader() });
};

export const createUserDuelChallenge = (opponentUserId) => {
  return axios.post(`${SOCIAL_API_URL}/duels/challenge`, { opponentUserId }, { headers: authHeader() });
};

export const acceptUserDuelChallenge = (duelId) => {
  return axios.post(`${SOCIAL_API_URL}/duels/${duelId}/accept`, {}, { headers: authHeader() });
};

export const declineUserDuelChallenge = (duelId) => {
  return axios.post(`${SOCIAL_API_URL}/duels/${duelId}/decline`, {}, { headers: authHeader() });
};

// Visual verification now supports multi-angle payloads (meal verification).
export const verifyQuestVisually = (questId, description, mediaPayload) => {
  const payload = {
    questId,
    description: description || ''
  };

  if (mediaPayload && typeof mediaPayload === 'object') {
    if (mediaPayload.angles) {
      payload.angles = mediaPayload.angles;
    }
    if (mediaPayload.imageBase64) {
      payload.imageBase64 = mediaPayload.imageBase64;
    }
  } else if (typeof mediaPayload === 'string') {
    payload.imageBase64 = mediaPayload;
  }

  return axios.post(`${API_URL}/quests/verify-visual`, payload, { headers: authHeader() });
};


export const getGoals = () => {
  return axios.get(`${API_URL}/goals`, { headers: authHeader() });
};


export const getTodaysQuests = () => {
  return axios.get(`${API_URL}/quests/today`, { headers: authHeader() });
};

export const getWeeklyShoppingList = () => {
  return axios.get(`${API_URL}/quests/shopping-list`, { headers: authHeader() });
};

export const toggleShoppingListItem = (item, checked) => {
  return axios.put(`${API_URL}/profile/shopping-list-check`, { item, checked }, { headers: authHeader() });
};

export const getQuestGenerationStatus = () => {
  return axios.get(`${API_URL}/quest-generation/status`, { headers: authHeader() });
};

export const getDashboardAggregate = () => {
  return axios.get(`${API_URL}/dashboard-aggregate`, { headers: authHeader() });
};

export const saveOnboardingProgress = (stepId, data, mode) => {
  return axios.patch(`${API_URL}/onboarding/progress`, { stepId, data, mode }, { headers: authHeader() });
};

export const completeOnboarding = (payload = {}) => {
  return axios.post(`${API_URL}/onboarding/complete`, payload, { headers: authHeader() });
};

export const submitFitnessAssessment = (payload = {}) => {
  return axios.post(`${API_URL}/fitness-assessment`, payload, { headers: authHeader() });
};

// EDIT THIS FUNCTION
export const getQuests = (options = {}) => {
  // Add cache-control headers to ensure we always get the latest quest status
  const headers = {
    ...authHeader(),
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  };

  const params = {};
  if (options.startFrom) {
    params.startFrom = options.startFrom;
  }
  if (options.endBefore) {
    params.endBefore = options.endBefore;
  }

  return axios.get(`${API_URL}/quests`, { headers, params });
};

export const getUserProfile = () => {
  return axios.get(`${API_URL}/profile`, { headers: authHeader() });
};

export const getAurasBalance = () => {
  return axios.get(`${API_URL}/auras/balance`, { headers: authHeader() });
};

export const getAurasTransactions = ({ page = 1, limit = 20 } = {}) => {
  return axios.get(`${API_URL}/auras/transactions`, { headers: authHeader(), params: { page, limit } });
};

export const purchaseAurasItem = (item) => {
  return axios.post(`${API_URL}/auras/purchase`, { item }, { headers: authHeader() });
};

export const refreshRevenueCatSubscription = () => {
  return axios.post(`${API_URL}/subscription/revenuecat/refresh`, {}, { headers: authHeader() });
};

export const activateAurasItem = (item) => {
  return axios.post(`${API_URL}/auras/activate`, { item }, { headers: authHeader() });
};

export const saveUserProfile = (userData) => {
  return axios.post(`${API_URL}/profile`, userData, { headers: authHeader() });
};

export const requestDataPortability = () => {
  return axios.get(`${API_URL}/gdpr/portability`, { headers: authHeader() });
};

export const deleteAccount = () => {
  return axios.delete(`${API_URL}/gdpr/delete`, { headers: authHeader() });
};

export const completeQuest = (questId) => {
  return axios.post(`${API_URL}/quests/complete`, { questId }, { headers: authHeader() });
};

export const moveSwapQuest = ({ questId, targetDate }) => {
  return axios.post(`${API_URL}/quests/move-swap`, { questId, targetDate }, { headers: authHeader() });
};


export const updatePlayerStats = (stats) => {
  return axios.post(`${API_URL}/player/stats`, stats, { headers: authHeader() });
};

export const updatePrestigeStats = (stats) => {
  return axios.post(`${API_URL}/player/prestige`, stats, { headers: authHeader() });
};

// --- ADD THE NEW USAGE FUNCTIONS ---
export const getAICoachUsage = () => {
  return axios.get(`${API_URL}/user/ai-coach-usage`, { headers: authHeader() });
};

const buildAiFeatureQuotaCacheKey = (features = []) => {
  if (!Array.isArray(features) || features.length === 0) {
    return 'all';
  }
  return [...new Set(features)].sort().join(',');
};

export const getAiFeatureQuotas = ({ features = null, force = false } = {}) => {
  const normalizedFeatures = Array.isArray(features) && features.length > 0
    ? [...new Set(features)]
    : null;
  const cacheKey = buildAiFeatureQuotaCacheKey(normalizedFeatures || []);
  const cached = aiFeatureQuotaResponseCache.get(cacheKey);

  if (!force && cached && (Date.now() - cached.cachedAt) < AI_FEATURE_QUOTA_CACHE_TTL_MS) {
    return Promise.resolve(cached.response);
  }

  const inflight = aiFeatureQuotaRequestCache.get(cacheKey);
  if (!force && inflight) {
    return inflight;
  }

  const request = axios.get(`${API_URL}/user/ai-feature-quotas`, {
    headers: authHeader(),
    params: normalizedFeatures ? { features: normalizedFeatures.join(',') } : undefined
  }).then((response) => {
    aiFeatureQuotaResponseCache.set(cacheKey, {
      response,
      cachedAt: Date.now()
    });
    return response;
  }).finally(() => {
    aiFeatureQuotaRequestCache.delete(cacheKey);
  });

  aiFeatureQuotaRequestCache.set(cacheKey, request);
  return request;
};

export const getImagesForQuest = (questId) => {
  return axios.post(`${API_URL}/quests/${questId}/images`, {}, { headers: authHeader() });
};

export const reportQuestImage = (questId, payload) => {
  return axios.post(`${API_URL}/quests/${questId}/images/report`, payload, { headers: authHeader() });
};

export const verifyQuestWithTimer = (questId, sessionData) => {
  return axios.post(`${API_URL}/quests/verify-timer`, { questId, sessionData }, { headers: authHeader() });
};

export const verifyMindActivation = (questId, sessionData) => {
  return axios.post(`${API_URL}/quests/verify-mind-activation`, { questId, sessionData }, { headers: authHeader() });
};

export const submitDailyMeals = (payload) => {
  return axios.post(`${API_URL}/quests/verify-meals/day`, payload, { headers: authHeader() });
};

export const logAICoachUsage = (durationSeconds) => {
  return axios.post(config.api.baseUrl + config.api.agentEndpoint + '/log-usage', { durationSeconds }, { headers: authHeader() });
};

export const syncCoachSession = (agentId) => {
  return axios.post(config.api.baseUrl + config.api.agentEndpoint + '/sync-conversation-duration', { agentId }, { headers: authHeader() });
};

export const getSignedUrlForAgent = (agentId) => {
  return axios.post(config.api.baseUrl + config.api.agentEndpoint + '/get-signed-url', { agentId }, { headers: authHeader() });
};

export const startFreeTierVoiceCoach = ({ audioBlob, durationMs, deviceInfo, conversationContextId }) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('durationMs', durationMs);
  if (deviceInfo) {
    formData.append('deviceInfo', JSON.stringify(deviceInfo));
  }
  if (conversationContextId) {
    formData.append('conversationContextId', conversationContextId);
  }

  return axios.post(`${config.api.baseUrl}/voice/coach`, formData, {
    headers: {
      ...authHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getVoiceCoachWsToken = () => {
  return axios.post(`${config.api.baseUrl}/voice/ws-token`, {}, { headers: authHeader() });
};

export const startVoiceCoachHttp = ({ audioBlob, durationMs, deviceInfo, conversationContextId, fallbackReason, utteranceId, signal }) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, audioBlob?.name || 'utterance.wav');
  if (durationMs) {
    formData.append('durationMs', durationMs);
  }
  if (deviceInfo) {
    formData.append('deviceInfo', JSON.stringify(deviceInfo));
  }
  if (conversationContextId) {
    formData.append('conversationContextId', conversationContextId);
  }
  if (fallbackReason) {
    formData.append('fallbackReason', fallbackReason);
  }
  if (utteranceId) {
    formData.append('utteranceId', utteranceId);
  }

  return axios.post(`${config.api.baseUrl}/voice/coach`, formData, {
    signal,
    headers: {
      ...authHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
};


// export const analyzeConversationForTools = (conversationId) => {
//   return axios.get(`${config.api.baseUrl + config.api.agentEndpoint}/analyze-conversation/${conversationId}`, { headers: authHeader() });
// };
